import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const STORAGE_KEY = 'ja:onboarding-done';

type Step = {
  selector: string;
  title: string;
  body: string;
  placement: 'top' | 'bottom';
};

const STEPS: Step[] = [
  {
    selector: '[data-tour="fab-cronometro"]',
    title: 'Cronômetro',
    body: 'Inicie seu treino aqui. Toque para começar.',
    placement: 'top',
  },
  {
    selector: '[data-tour="nav-alunos"]',
    title: 'Alunos',
    body: 'Cadastre seus alunos para começar a montar treinos.',
    placement: 'top',
  },
  {
    selector: '[data-tour="nav-agenda"]',
    title: 'Agenda',
    body: 'Organize horários e veja sua próxima sessão.',
    placement: 'top',
  },
];

type Rect = { top: number; left: number; width: number; height: number };

export const OnboardingCoachMarks = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [active, setActive] = useState(true);
  const rafRef = useRef<number | null>(null);

  const step = STEPS[stepIndex];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return;
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Defer first measurement until after paint (FAB may mount after Timer).
  useLayoutEffect(() => {
    if (!active) return;
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      attempts += 1;
      const el = document.querySelector(step.selector) as HTMLElement | null;
      const r = el?.getBoundingClientRect();
      if (el && r && (r.width > 0 || r.height > 0)) {
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        return;
      }
      if (attempts < 30) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stepIndex, active, step]);

  // Re-measure on resize / orientation / scroll (debounced via rAF).
  // Note: mobile virtual keyboard fires resize — re-measure handles it.
  useEffect(() => {
    if (!active) return;
    let pending = false;
    const onChange = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        measure();
      });
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [active, measure]);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setActive(false);
  }, []);

  const next = useCallback(() => {
    if (stepIndex >= STEPS.length - 1) {
      finish();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, finish]);

  if (!active) return null;

  const padding = 8;
  const spotlight = rect
    ? {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  // Tooltip positioning
  const tooltipWidth = 280;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 640;
  let tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    width: tooltipWidth,
    maxWidth: 'calc(100vw - 24px)',
    zIndex: 10001,
  };
  if (spotlight) {
    const centerX = spotlight.left + spotlight.width / 2;
    let left = centerX - tooltipWidth / 2;
    left = Math.max(12, Math.min(left, vw - tooltipWidth - 12));
    if (step.placement === 'top') {
      tooltipStyle.top = Math.max(12, spotlight.top - 12 - 140);
    } else {
      tooltipStyle.top = Math.min(vh - 160, spotlight.top + spotlight.height + 12);
    }
    tooltipStyle.left = left;
  } else {
    tooltipStyle.top = vh / 2 - 80;
    tooltipStyle.left = vw / 2 - tooltipWidth / 2;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Onboarding passo ${stepIndex + 1} de ${STEPS.length}`}
      className="fixed inset-0 z-[10000] pointer-events-none"
    >
      {/* Overlay with spotlight via box-shadow */}
      {spotlight ? (
        <div
          className="fixed pointer-events-auto rounded-2xl transition-all duration-200"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            boxShadow: '0 0 0 9999px hsl(0 0% 0% / 0.72)',
            outline: '2px solid hsl(var(--thai-gold))',
            outlineOffset: '2px',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          className="fixed inset-0 pointer-events-auto"
          style={{ background: 'hsl(0 0% 0% / 0.72)' }}
        />
      )}

      {/* Tooltip */}
      <div
        style={tooltipStyle}
        className="pointer-events-auto rounded-2xl border border-hairline bg-canvas/95 backdrop-blur-xl p-5 shadow-elev-2"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-manrope font-bold tracking-wider uppercase text-thai-gold">
            Passo {stepIndex + 1} de {STEPS.length}
          </span>
          <button
            onClick={finish}
            className="text-xs font-medium text-ink-low hover:text-ink-mid transition-colors"
          >
            Pular
          </button>
        </div>
        <h3 className="text-base font-semibold text-ink-high mb-1">{step.title}</h3>
        <p className="text-sm text-ink-mid mb-4 leading-relaxed">{step.body}</p>
        <div className="flex justify-end">
          <button
            onClick={next}
            className="px-4 py-2 rounded-lg bg-thai-gold text-black text-sm font-semibold hover:opacity-90 transition-opacity min-h-[44px] min-w-[88px]"
          >
            {stepIndex === STEPS.length - 1 ? 'Concluir' : 'Próximo'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const hasCompletedCoachMarks = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};